import css from './CreateUserForm.module.css';

import TextInput from '../../Common/TextInput/TextInput';
import Validators from '../../../utility/validators';
import {
  DataStatus,
  ErrorCode,
  FormField,
  FormStatus,
} from '../../../types/appTypes';
import { useForm } from '../../../hooks/useForm';
import { useData } from '../../../hooks/useData';
import { Registration } from '../../../types/appModels';
import DataForm from '../../Common/DataForm/DataForm';
import { useNavigate } from 'react-router-dom';
import PasswordPolicy from '../PasswordPolicy/PasswordPolicy';

type CreateUserFormProps = {
  registrationId: string | null;
};

type CreateUserFormFields = {
  password: FormField<string>;
  copyPassword: FormField<string>;
};

const CreateUserForm = ({ registrationId }: CreateUserFormProps) => {
  const navigate = useNavigate();
  const { dataState: regDataState } = useData<Registration>({
    endpoint: `${process.env.REACT_APP_API_REGISTRATION}/${registrationId}`,
    initialData: {
      regEmail: '',
      name: '',
      company: '',
      expired: false,
      apiRequest: false,
      atlasRequest: false,
    },
    initialStatus: DataStatus.Loading,
    loadWhen: registrationId !== null && registrationId !== '',
  });
  const { fields, formState, setFields, setFormState } =
    useForm<CreateUserFormFields>({
      initialFields: {
        password: { value: '', valid: false },
        copyPassword: { value: '', valid: false },
      },
    });

  const formIsValid = fields.password.valid && fields.copyPassword.valid;

  const formRequiredInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const valid = Validators.required(value);
    setFields((pv) => {
      return { ...pv, [name]: { value: value, valid: valid } };
    });
  };

  const formSubmitHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !formIsValid ||
      registrationId === null ||
      registrationId.trim() === ''
    ) {
      return;
    }

    if (
      !Validators.valuesMatch(fields.password.value, fields.copyPassword.value)
    ) {
      setFormState({
        status: FormStatus.Fail,
        error: {
          code: ErrorCode.passwordMismatch,
          message: 'The passwords do not match',
        },
      });
      return;
    }

    if (!Validators.passwordPolicy(fields.password.value)) {
      setFormState({
        status: FormStatus.Fail,
        error: {
          code: ErrorCode.passwordPolicyViolation,
          message: 'The password does not adhere to the policy',
        },
      });
      return;
    }

    setFormState({ status: FormStatus.Submitting });

    fetch(`${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: registrationId.trim(),
        password: fields.password.value
      }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.status === 200) {
          setFormState({ status: FormStatus.Success });
        } else if (response.status === 404) {
          setFormState({
            status: FormStatus.NotFound,
            error: {
              code: data.code,
              message:
                'We could not find your registration.  It may have already been processed, has not been approved, or it has expired.',
            },
          });
        } else {
          setFormState({
            status: FormStatus.Fail,
            error: {
              code: data.code,
              message:
                data.code === ErrorCode.passwordPolicyViolation
                  ? 'The password does not adhere to the policy'
                  : 'An error occurred and your account could not be created.',
            },
          });
        }
      })
      .catch((err) => {
        setFormState({
          status: FormStatus.Error,
          error: {
            code: ErrorCode.unknown,
            message: 'An error occurred and your account could not be created.',
          },
        });
      });
  };

  const successActionHandler = (event: React.MouseEvent) => {
    navigate('/');
  };

  return (
    <DataForm
      title='Create Account'
      successMessage='Your account has been created.'
      successActionMessage='Login Now'
      successAction={successActionHandler}
      submitErrorMessage={formState.error?.message}
      dataLoadErrorMessage='An error occurred and your registration can not be processed.'
      dataLoadNotFoundMessage='We could not find your registration.  It may have already been processed, has not been approved, or it has expired.'
      secondaryContainerClass={css.container}
      status={formState.status}
      dataStatuses={[regDataState.status]}
      onSubmit={formSubmitHandler}
    >
      <div className={css.userData}>
        <div className={css.userDataItem}>
          <label>Email</label>
          <p>{regDataState.data!.regEmail}</p>
        </div>
        <div className={css.userDataItem}>
          <label>Name</label>
          <p>{regDataState.data!.name}</p>
        </div>
        <div className={css.userDataItem}>
          <label>Company</label>
          <p>{regDataState.data!.company}</p>
        </div>
      </div>
      <PasswordPolicy />
      <TextInput
        placeholder='Password'
        type='password'
        name='password'
        value={fields.password.value}
        valid={fields.password.valid}
        onChange={formRequiredInputChangeHandler}
      />
      <TextInput
        placeholder='Copy Password'
        type='password'
        name='copyPassword'
        value={fields.copyPassword.value}
        valid={fields.copyPassword.valid}
        onChange={formRequiredInputChangeHandler}
      />
      <div className={css.actions}>
        <button type='submit' disabled={!formIsValid}>
          Create
        </button>
      </div>
    </DataForm>
  );
};

export default CreateUserForm;